import { API_DOMAIN } from 'consts';
import last from 'lodash/last';
import replace from 'lodash/replace';
import split from 'lodash/split';
import startCase from 'lodash/startCase';
import startsWith from 'lodash/startsWith';

/**
 * Extract a human-readable title from a presigned S3 URL.
 * Strips UUID prefix and file extension, then converts to title case.
 * e.g. "a1b2c3d4-crude-dashboard.jsx" → "Crude Dashboard"
 */
export const getArtifactTitle = (url: string): string => {
  try {
    const pathname = new URL(url, 'http://localhost').pathname;
    const filename = last(split(pathname, '/')) || 'Artifact';
    // Strip all file extensions (e.g. "dashboard.spec.jsonl" → "dashboard")
    // so compound extensions like .spec.jsonl don't leak into the title.
    const base = replace(filename, /\..+$/, '');
    // Strip leading hex/UUID prefix separated by dash or underscore
    // e.g. "5b9344a42a26470e91b8895582150fb1_brent_dashboard" or "a1b2c3d4-crude-dashboard"
    const withoutUuid = replace(base, /^[0-9a-f]{8,}[-_]/, '');
    return startCase(withoutUuid) || 'Artifact';
  } catch {
    return 'Artifact';
  }
};

/**
 * Extract the download filename from a presigned S3 URL: last path segment
 * with the UUID prefix stripped but the extension kept.
 * e.g. "5b9344a4..._brent_settles.csv" → "brent_settles.csv"
 */
export const getArtifactFilename = (url: string): string => {
  try {
    const pathname = new URL(url, 'http://localhost').pathname;
    const filename = last(split(pathname, '/')) || 'artifact';
    return replace(decodeURIComponent(filename), /^[0-9a-f]{8,}[-_]/, '') || 'artifact';
  } catch {
    return 'artifact';
  }
};

type ArtifactTypeInfo = {
  icon: 'code' | 'dashboard' | 'image' | 'notebook' | 'presentation' | 'table';
  label: string;
};

/**
 * Return icon type and label string based on media type / URL.
 */
export const getArtifactTypeInfo = (
  mediaType: string | undefined,
  url: string | undefined,
): ArtifactTypeInfo => {
  if (mediaType === 'application/vnd.specstream+jsonl') {
    return { icon: 'dashboard', label: 'Dashboard' };
  }
  if (mediaType === 'text/html') {
    return { icon: 'dashboard', label: 'Dashboard · HTML' };
  }
  if (isJsxFile(mediaType, url)) {
    return { icon: 'code', label: 'Code · JSX' };
  }
  if (isNotebookFile(mediaType, url)) {
    return { icon: 'notebook', label: 'Notebook' };
  }
  if (isPptxFile(mediaType, url)) {
    return { icon: 'presentation', label: 'Slides · PowerPoint' };
  }
  if (isExcelFile(mediaType, url)) {
    return { icon: 'table', label: 'Table · Excel' };
  }
  if (isCsvFile(mediaType, url)) {
    return { icon: 'table', label: 'Table · CSV' };
  }
  if (mediaType && startsWith(mediaType, 'image/')) {
    return { icon: 'image', label: 'Image' };
  }
  return { icon: 'code', label: 'File' };
};

/**
 * Centralized JSX detection — matches by media type or URL extension.
 */
export const isJsxFile = (
  mediaType: string | undefined,
  url: string | undefined,
): boolean =>
  mediaType === 'text/jsx' || /\.jsx(?:\?|$)/i.test(url || '');

// The predicates below also match by URL extension so manifest entries
// persisted before the media type was registered (stored as
// application/octet-stream) still route to the right renderer.

export const isNotebookFile = (
  mediaType: string | undefined,
  url: string | undefined,
): boolean =>
  mediaType === 'application/x-ipynb+json' || /\.ipynb(?:\?|$)/i.test(url || '');

export const isCsvFile = (
  mediaType: string | undefined,
  url: string | undefined,
): boolean =>
  mediaType === 'text/csv'
  || mediaType === 'text/tab-separated-values'
  || /\.[ct]sv(?:\?|$)/i.test(url || '');

export const isExcelFile = (
  mediaType: string | undefined,
  url: string | undefined,
): boolean =>
  mediaType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  // Prefix match covers .xls plus the .xlsm macro-enabled variant
  // ("…ms-excel.sheet.macroEnabled.12"), whose casing differs by OS.
  || startsWith(mediaType, 'application/vnd.ms-excel')
  || /\.xls[xm]?(?:\?|$)/i.test(url || '');

// PowerPoint decks have no inline preview — the ArtifactViewer renders a
// download-only card for these.
export const isPptxFile = (
  mediaType: string | undefined,
  url: string | undefined,
): boolean =>
  mediaType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  || /\.pptx(?:\?|$)/i.test(url || '');

export const isTabularFile = (
  mediaType: string | undefined,
  url: string | undefined,
): boolean => isCsvFile(mediaType, url) || isExcelFile(mediaType, url);

/** The artifact open in the viewer pane. */
export type ActiveArtifact = {
  url: string;
  mediaType: string | undefined;
  /** In-flight SpecStream patches for a dashboard still streaming (url is the literal 'streaming'). */
  patches?: object[];
  /** Persisted spec file from an earlier turn that the streamed patches merge onto. */
  baseUrl?: string;
};

/** Presigned S3 URLs are absolute; relative paths need the API domain prefix. */
export const resolveFileUrl = (url: string): string =>
  (startsWith(url, 'http') ? url : `${API_DOMAIN}${url}`);

/**
 * Whether a transcript artifact card corresponds to the artifact open in the
 * pane. A streamed spec dashboard's active url is the literal 'streaming'
 * (in-memory patches), never the card's persisted-file URL, so match it by
 * media type too — otherwise clicking the card remounts the viewer it
 * already shows.
 */
export const isArtifactActive = (
  active: ActiveArtifact | null,
  fileUrl: string,
  mediaType: string | undefined,
): boolean =>
  active?.url === fileUrl
  || (active?.url === 'streaming' && mediaType === 'application/vnd.specstream+jsonl');
